import { useCallback, useEffect, useState } from 'react'
import { Loader2, Undo2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BankStatementDto } from '@jr2os/shared'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import { useTableSort } from '../../lib/useTableSort'

/** Pasa por aquí tanto el botón del resultado de importar como esta lista. */
export async function undoStatement(
  statement: Pick<BankStatementDto, 'id' | 'fileName'>,
  t: (key: string, opts?: Record<string, unknown>) => string
): Promise<boolean> {
  const ok = await confirmDialog({
    title: t('statements.undoTitle'),
    message: t('statements.undoBody', { file: statement.fileName }),
    confirmLabel: t('statements.undo'),
    tone: 'danger',
  })
  if (!ok) return false
  try {
    await api.delete(`/banking/statements/${statement.id}`)
    toast.success(t('statements.undone'))
    return true
  } catch (err) {
    const body = err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
    toast.error(body?.error ?? (err instanceof Error ? err.message : t('errors.generic')))
    return false
  }
}

/**
 * Las importaciones hechas en una cuenta, para poder deshacer una que se leyó mal
 * aunque ya se haya cerrado el resultado. Solo se puede deshacer si nadie ha tocado
 * ninguno de sus movimientos: la API lo comprueba y lo dice.
 */
export default function StatementsModal({
  accountId,
  onClose,
  onChanged,
}: {
  accountId: string
  onClose: () => void
  onChanged: () => Promise<void> | void
}) {
  const { t } = useTranslation(['banking', 'common'])
  const [items, setItems] = useState<BankStatementDto[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  // Modal: el orden vive en memoria, la URL no pinta nada aquí. Sin orden elegido, el de siempre (lo último importado primero).
  const { rows, header } = useTableSort(
    items,
    {
      imported: (s) => s.importedAt,
      file: (s) => s.fileName,
      period: (s) => s.fromDate,
      rows: (s) => s.importedCount,
    },
    { persist: 'state' }
  )

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ items: BankStatementDto[] }>(
        `/banking/statements?bankAccountId=${encodeURIComponent(accountId)}`
      )
      setItems(r.items)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
      setItems([])
    }
  }, [accountId, t])

  useEffect(() => {
    void load()
  }, [load])

  const onUndo = async (st: BankStatementDto) => {
    setBusyId(st.id)
    try {
      if (await undoStatement(st, t)) {
        await load()
        await onChanged()
      }
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('statements.title')}
      description={t('statements.hint')}
      maxWidth="max-w-3xl"
    >
      {items === null ? (
        <p className="text-sm t-muted flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t('loading')}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm t-muted">{t('statements.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left t-muted uppercase tracking-wider">
                {header('imported', t('statements.imported'), { className: 'px-2 py-1.5' })}
                {header('file', t('statements.file'), { className: 'px-2 py-1.5' })}
                {header('period', t('import.period'), { className: 'px-2 py-1.5' })}
                {header('rows', t('statements.rows'), { align: 'right', className: 'px-2 py-1.5' })}
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((st) => (
                <tr key={st.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                    {st.importedAt.slice(0, 10)}
                    {st.importedByName && (
                      <div className="font-sans t-faint">{st.importedByName}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 t-primary max-w-[16rem] truncate" title={st.fileName}>
                    {st.fileName}
                    <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full border border-base t-muted">
                      {st.format}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                    {st.fromDate.slice(0, 10)} → {st.toDate.slice(0, 10)}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    {t('statements.rowsDetail', {
                      imported: st.importedCount,
                      total: st.transactionCount,
                    })}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => void onUndo(st)}
                      disabled={busyId !== null}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-base hover:bg-hover t-primary disabled:opacity-60"
                    >
                      {busyId === st.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Undo2 size={12} />
                      )}
                      {t('statements.undo')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  )
}
